import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { PublicClientApplication, EventType } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import App from './App'
import { AuthProvider } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import { ToastProvider } from './context/ToastContext'
import { BrandingProvider } from './context/BrandingContext'
import { DisplayPreferencesProvider } from './context/DisplayPreferencesContext'
import { OpCoProvider } from './context/OpCoContext'
import { getRouterBasename } from './config/opcoWorkspace'
import { msalConfig, isMsalConfigured } from './config/msalConfig'
import './index.css'

// Error Boundary for debugging
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', color: 'red' }}>
          <h1>Something went wrong.</h1>
          <pre>{this.state.error?.toString()}</pre>
        </div>
      )
    }
    return this.props.children
  }
}

// Initialize MSAL instance (only if configured)
let msalInstance = null
if (isMsalConfigured()) {
  msalInstance = new PublicClientApplication(msalConfig)
}

// MSAL wrapper component
import { FeaturesProvider } from './contexts/FeaturesContext'

const MsalAppWrapper = () => {
  const [isMsalReady, setIsMsalReady] = React.useState(false)
  const [msalInitError, setMsalInitError] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    let callbackId = null

    msalInstance.initialize()
      .then(() => {
        if (cancelled) return

        callbackId = msalInstance.addEventCallback((event) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
            msalInstance.setActiveAccount(event.payload.account)
          }
        })

        if (!msalInstance.getActiveAccount()) {
          const [cachedAccount] = msalInstance.getAllAccounts()
          if (cachedAccount) msalInstance.setActiveAccount(cachedAccount)
        }
        setIsMsalReady(true)
      })
      .catch((error) => {
        console.error('Failed to initialize Microsoft Entra authentication:', error)
        if (!cancelled) setMsalInitError(true)
      })

    return () => {
      cancelled = true
      if (callbackId) msalInstance.removeEventCallback(callbackId)
    }
  }, [])

  if (msalInitError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Sign-in could not be initialized</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Please reload the page and try again.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 rounded-lg bg-navy-800 px-4 py-2 text-sm font-medium text-white hover:bg-navy-900"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (!isMsalReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <p className="text-gray-600 dark:text-gray-400">Initializing authentication...</p>
      </div>
    )
  }

  return (
    <ThemeProvider>
      <ToastProvider>
        <MsalProvider instance={msalInstance}>
          <AuthProvider>
            <FeaturesProvider>
            <OpCoProvider>
            <BrandingProvider>
              <DisplayPreferencesProvider>
                <App />
              </DisplayPreferencesProvider>
            </BrandingProvider>
            </OpCoProvider>
            </FeaturesProvider>
          </AuthProvider>
        </MsalProvider>
      </ToastProvider>
    </ThemeProvider>
  )
}

// Dev mode wrapper - no MSAL
const DevAppWrapper = () => {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <FeaturesProvider>
          <OpCoProvider>
          <BrandingProvider>
            <DisplayPreferencesProvider>
              <App />
            </DisplayPreferencesProvider>
          </BrandingProvider>
          </OpCoProvider>
          </FeaturesProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  )
}

// OpCo workspaces: inside /o/<code>/ the router runs under that prefix, so
// every existing route and link works unchanged within the workspace. Flag
// off (or no prefix) => basename '' (identical to before).
const routerBasename = getRouterBasename()

ReactDOM.createRoot(document.getElementById('root')).render(
  <ErrorBoundary>
    <BrowserRouter basename={routerBasename || undefined} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {isMsalConfigured() ? (
        <MsalAppWrapper />
      ) : (
        <DevAppWrapper />
      )}
    </BrowserRouter>
  </ErrorBoundary>
)
